import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCalendarPlus, faPenToSquare, faWallet, faCalendarXmark, faUserPen, faUserPlus, faChevronRight, faSpinner, faFileInvoice } from '@fortawesome/free-solid-svg-icons';
import { resolveRequestPaymentRail, resolvePaymentStatus } from './requestPaymentMethod';
import './pendingClientRequests.css';

// THE ONE ROW (2026-07-18).
//
// The Home panel and the Requests page rendered the same queue with two
// independently-written row markups — different class trees, different chips,
// different date formats, and only Home showed the payment-rail chip and the
// "payment in review — do not approve yet" hold. That last one matters: the
// page a reviewer is most likely to work from was the page that omitted the
// warning. Both surfaces now render THIS component, so a chip added here can
// never again exist on one screen and not the other.
//
// The Home markup (.pcr-row) is the canonical look; the Requests page's extras
// (status chip, reviewer attribution, rejection reason, result link) arrive as
// optional props rather than a forked copy.

export const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PAYMENT_METHOD_LABELS = { bank_transfer: 'Bank Transfer', online_payment: 'Online Payment' };

export const TYPE_META = {
    create_property: { label: 'New Property', icon: faBuilding },
    edit_property: { label: 'Edit Property', icon: faPenToSquare },
    create_booking: { label: 'New Booking', icon: faCalendarPlus },
    edit_booking: { label: 'Edit Booking', icon: faPenToSquare },
    cancel_booking: { label: 'Cancel Booking', icon: faCalendarXmark },
    edit_client_profile: { label: 'Edit Account', icon: faUserPen },
    edit_client_tasks: { label: 'Edit Tasks (all units)', icon: faPenToSquare },
    // Self-serve signup (PLAN_registration.md) — account already live;
    // the review modal only acknowledges.
    register_client: { label: 'New Client Registration', icon: faUserPlus },
    // Multi-billing (PLAN_multi_billing.md) — client asks for an extra
    // billing entity; approval creates the Zoho customer.
    add_billing_profile: { label: 'New Billing Entity', icon: faFileInvoice },
    // …or asks to change an existing one; approval updates DB + Zoho.
    edit_billing_profile: { label: 'Edit Billing Entity', icon: faFileInvoice },
};

export const DEPOSIT_CHIP = {
    pending: { label: 'Deposit pending', className: 'pcr-deposit-chip--pending' },
    approved: { label: 'Deposit approved', className: 'pcr-deposit-chip--approved' },
    rejected: { label: 'Deposit rejected', className: 'pcr-deposit-chip--rejected' },
};

const CHANGE_LABELS = {
    booking_start: 'time',
    duration_hours: 'duration',
    required_cleaners: 'cleaners',
    notes: 'notes',
    bedrooms: 'bedrooms',
    bathrooms: 'bathrooms',
    single_beds: 'beds',
    double_beds: 'beds',
    super_king_beds: 'beds',
    property_access: 'access',
    wifi_name: 'wifi',
    wifi_password: 'wifi',
    address_line_1: 'building',
    unit_number: 'unit',
    area: 'area',
    is_beach_towel: 'amenities',
    is_dolce_gusto: 'amenities',
    is_nespresso: 'amenities',
    type_id: 'service',
    property_id: 'property',
    client_type: 'account type',
    company_name: 'company',
    contact_number: 'contact number',
    trn_number: 'TRN',
    is_vat_registered: 'VAT status',
    city: 'city',
    state: 'state',
    country: 'country',
    documents: 'documents',
    client_tasks: 'tasks',
    property_tasks: 'tasks',
    tasks: 'tasks',
    setup_photos: 'setup photos',
};

function changedSummary(p) {
    const fields = Array.isArray(p.changed_fields) ? p.changed_fields : [];
    const labels = [...new Set(fields.map((f) => CHANGE_LABELS[f] || f))];
    return labels.length ? `changing ${labels.join(', ')}` : 'change request';
}

export function requestSummary(request) {
    const p = request.payload || {};
    if (request.request_type === 'create_property') {
        return `${p.address_line_1 || '—'} · Unit ${p.unit_number || '—'} · ${p.bedrooms === 0 ? 'Studio' : `${p.bedrooms} BR`}`;
    }
    if (request.request_type === 'create_booking') {
        return `${p.property_label || ''} · ${p.type_label || ''} · ${(p.booking_start || '').slice(0, 16)}`;
    }
    if (request.request_type === 'edit_booking') {
        return `${p.property_label || ''} · Booking #${request.target_id} · ${changedSummary(p)}`;
    }
    if (request.request_type === 'edit_property') {
        return `${p.property_label || ''} · ${changedSummary(p)}`;
    }
    if (request.request_type === 'cancel_booking') {
        return `${p.property_label || ''} · Booking #${request.target_id} · ${(p.booking_start || '').slice(0, 16)}`;
    }
    if (request.request_type === 'edit_client_profile') {
        return `Account details · ${changedSummary(p)}`;
    }
    if (request.request_type === 'edit_client_tasks') {
        const n = Array.isArray(p.tasks) ? p.tasks.length : 0;
        return `All-units tasks · ${n} task${n === 1 ? '' : 's'} after change`;
    }
    if (request.request_type === 'register_client') {
        return `${p.client_type === 'business' ? (p.company_name || 'Business') : 'Individual'} · ${p.email || ''} · WhatsApp verified`;
    }
    if (request.request_type === 'add_billing_profile' || request.request_type === 'edit_billing_profile') {
        const isBiz = p.entity_type === 'business';
        const name = p.label
            || (isBiz
                ? (p.legal_name || 'Business')
                : [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Individual');
        const base = `${name} · ${isBiz ? 'Business' : 'Individual'}${p.is_vat_registered ? ` · TRN ${p.trn_number || '—'}` : ''}`;
        return request.request_type === 'edit_billing_profile' ? `${base} · change request` : base;
    }
    return '';
}

// The entity a request points at — for the row's reference chip AND its click
// target. Bookings/properties use result_id (the created row) or target_id (the
// edited/cancelled one); account & all-units-task edits point at the client.
// Returns { kind, id, label } or null (e.g. a still-pending create has no id
// yet). Callers turn `kind` into an admin (/bookings/:id) or client
// (/client/bookings/:id) path. (2026-07-21)
export function requestEntity(request) {
    const t = request.request_type;
    if (t === 'create_booking' || t === 'edit_booking' || t === 'cancel_booking') {
        const id = request.result_id || request.target_id;
        return id ? { kind: 'booking', id, label: `Booking #${id}` } : null;
    }
    if (t === 'create_property' || t === 'edit_property') {
        const id = request.result_id || request.target_id;
        return id ? { kind: 'property', id, label: `Property #${id}` } : null;
    }
    if (t === 'edit_client_profile' || t === 'edit_client_tasks' || t === 'register_client'
        || t === 'add_billing_profile' || t === 'edit_billing_profile') {
        return request.client_id ? { kind: 'client', id: request.client_id, label: `Client #${request.client_id}` } : null;
    }
    return null;
}

export function timeAgo(dateString) {
    const ms = Date.now() - new Date(dateString).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return `${Math.max(mins, 0)}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
}

// Whole-row click affordance. When `onRowClick` is set the entire row is a
// button (cursor, hover, keyboard-activatable) with a trailing chevron — the
// "Review" buttons and "View #" links are gone; clicking anywhere on the row
// opens the review (pending) or jumps to the entity (2026-07-21).
function rowClickProps(onRowClick) {
    if (typeof onRowClick !== 'function') return { className: '' };
    return {
        className: ' pcr-row--clickable',
        role: 'button',
        tabIndex: 0,
        onClick: onRowClick,
        onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(); }
        },
    };
}

function RowChevron({ busy }) {
    return (
        <div className="pcr-row-chevron" aria-hidden="true">
            <FontAwesomeIcon icon={busy ? faSpinner : faChevronRight} spin={!!busy} />
        </div>
    );
}

/**
 * One queue row, for a client request or a pending deposit.
 *
 * @param {object}   props
 * @param {'request'|'deposit'} props.kind
 * @param {object}   props.data          the request row or deposit row
 * @param {string}   props.metaText      trailing meta line (time / attribution)
 * @param {object}   [props.statusChip]  { label, className } — history only
 * @param {string}   [props.entityRef]   "Booking #123" / "Property #45" / "Client #7"
 * @param {function} [props.onRowClick]  makes the WHOLE row clickable
 * @param {boolean}  [props.busy]        show a spinner instead of the chevron
 * @param {node}     [props.extraMeta]   reviewer attribution etc.
 * @param {node}     [props.footer]      rejection/cancellation reason
 */
export default function RequestRow({
    kind, data, metaText, statusChip,
    entityRef = null, onRowClick = null, busy = false,
    extraMeta = null, footer = null,
}) {
    const clickable = typeof onRowClick === 'function';
    const click = rowClickProps(onRowClick);

    if (kind === 'deposit') {
        const dep = data;
        return (
            <div
                className={`pcr-row pcr-row--deposit${click.className}`}
                role={click.role} tabIndex={click.tabIndex} onClick={click.onClick} onKeyDown={click.onKeyDown}
            >
                <div className="pcr-row-icon pcr-row-icon--deposit">
                    <FontAwesomeIcon icon={faWallet} />
                </div>
                <div className="pcr-row-main">
                    <div className="pcr-row-title">
                        <span className="pcr-type-chip pcr-type-chip--deposit">Deposit</span>
                        <span className="pcr-req-id" title="Top-up reference">TOP-{dep.id}</span>
                        <strong>{dep.client_name}</strong>
                        {statusChip && (
                            <span className={`pcr-status-chip ${statusChip.className || ''}`}>{statusChip.label}</span>
                        )}
                    </div>
                    <p className="pcr-row-summary">
                        {money(dep.amount)}
                        {Number(dep.bonus_amount) > 0 ? ` (+${money(dep.bonus_amount)} bonus)` : ''}
                        {' · '}{PAYMENT_METHOD_LABELS[dep.payment_method] || dep.payment_method}
                        {dep.bank_reference ? ` · Ref ${dep.bank_reference}` : ''}
                    </p>
                    <span className="pcr-row-meta">{metaText}</span>
                </div>
                {clickable && <RowChevron busy={busy} />}
            </div>
        );
    }

    const request = data;
    const meta = TYPE_META[request.request_type] || { label: request.request_type, icon: faPenToSquare };
    // Booking requests carry money — show the rail (card / wallet / auto-charge)
    // on the row itself so nobody has to open the modal to know how it was paid,
    // and flag a gateway hold that must NOT be approved yet.
    const isBookingRequest = request.request_type === 'create_booking' || request.request_type === 'edit_booking';
    const rail = isBookingRequest ? resolveRequestPaymentRail(request) : null;
    const payStatus = isBookingRequest ? resolvePaymentStatus(request) : null;

    return (
        <div
            className={`pcr-row${click.className}`}
            role={click.role} tabIndex={click.tabIndex} onClick={click.onClick} onKeyDown={click.onKeyDown}
        >
            <div className="pcr-row-icon">
                <FontAwesomeIcon icon={meta.icon} />
            </div>
            <div className="pcr-row-main">
                <div className="pcr-row-title">
                    <span className="pcr-type-chip">{meta.label}</span>
                    <span className="pcr-req-id" title="Request reference">REQ-{request.id}</span>
                    {entityRef && (
                        <span className="pcr-entity-ref" title="Goes to this record">{entityRef}</span>
                    )}
                    <strong>{request.client_display_name || request.client_company_name || `Client #${request.client_id}`}</strong>
                    {statusChip && (
                        <span className={`pcr-status-chip ${statusChip.className || ''}`}>{statusChip.label}</span>
                    )}
                    {rail && (
                        <span
                            className={`pcr-payrail-chip pcr-payrail-chip--${rail.key === 'card' || rail.key === 'wallet_card' ? 'card' : rail.key}`}
                            title={rail.detail}
                        >
                            {rail.label}
                        </span>
                    )}
                    {payStatus?.tone === 'warn' && (
                        <span className="pcr-payrail-chip pcr-payrail-chip--hold" title={payStatus.detail}>
                            {payStatus.label}
                        </span>
                    )}
                    {/* Booking notices recorded at submission (routes/services/bookingNotice.js):
                        the client ticked that this is subject to availability. */}
                    {request.payload?.booking_notice?.last_minute === true && (
                        <span
                            className="pcr-payrail-chip pcr-payrail-chip--lastminute"
                            title="Booked for the same day, or after 20:00 for next-day peak hours — subject to availability"
                        >
                            Last minute
                        </span>
                    )}
                    {request.payload?.booking_notice?.outside_office_hours === true
                        && (!request.status || ['pending', 'awaiting_payment'].includes(request.status)) && (
                        <span
                            className="pcr-payrail-chip pcr-payrail-chip--afterhours"
                            title="Requested outside office hours (09:00–20:00) — the client was told it is reviewed from 09:00"
                        >
                            After hours
                        </span>
                    )}
                    {request.linked_deposit_id && DEPOSIT_CHIP[request.linked_deposit_status] && (
                        <span className={`pcr-deposit-chip ${DEPOSIT_CHIP[request.linked_deposit_status].className}`}>
                            {DEPOSIT_CHIP[request.linked_deposit_status].label}
                        </span>
                    )}
                </div>
                <p className="pcr-row-summary">
                    {requestSummary(request)}
                </p>
                <span className="pcr-row-meta">
                    {metaText}
                    {extraMeta}
                </span>
                {footer}
            </div>
            {clickable && <RowChevron busy={busy} />}
        </div>
    );
}
